import { useMemo } from "react";
import { useTheme } from "@mui/material";

import { Topic } from "./TopicsTreeMap";

interface TreeMapNodeProps {
  colors?: string[];
  data?: DataItem[];
  showClusterLabel?: boolean;
}

interface DataItem {
  name: string;
  children: { name: string, size: number, topic: Topic }[];
}

export default function TreeMapNode(props: TreeMapNodeProps): JSX.Element {
  const theme = useTheme();

  // Customized Treemap cell content
  const CustomizedTreeMapNode = useMemo(() => {
    const SVGContent = (props: { props: Record<string, any> }) => {
      const { depth, x, y, width, height, index, colors, name, root } = props.props;

      // Clusters aren't zero-based indexing, so first cluster will be "1"
      const fillColor = (props.props.topic && props.props.topic.cluster_id)
        ? colors[props.props.topic.cluster_id - 1]
        : theme.palette.grey[400];

      // SVGs can't render emojis so we check if topic is emoji  
      const emojiRegex = /\p{Extended_Pictographic}/u;        
      const isEmoji = (root.depth === 1 && name.match(emojiRegex)?.length > 0) ? true : false;

      if (isEmoji) {
        return (
          <g>
            <foreignObject x={x} y={y} width={width} height={height}>
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '1px solid #fff',
                  cursor: 'pointer'
                }}
              >
                {depth > 1 && (
                  <span style={{ color: '#fff', fontSize: 20, fontWeight: 'bold', textAlign: 'center' }}>
                    {name}
                  </span>
                )}
              </div>
            </foreignObject>
          </g>
        );
      } else {
        return (
          <>
            <g>
              {/* Color Box */}
              <rect
                x={x}
                y={y}
                width={width}
                height={height}
                fill={fillColor}
                stroke="#fff"
              />
              {/* Topic Label */}
              {depth > 1 && (
                <svg
                  width={width}
                  height={height}
                  x={x}
                  y={y}
                >
                  <text
                    x={width / 2}
                    y="50%"
                    dominantBaseline="middle"
                    fontSize={14}
                    fill="#fff"
                    textAnchor="middle"
                  >
                    {name}
                  </text>
                </svg>
              )}
            </g>
            {/* Cluster Group Label */}
            {props.props.showClusterLabel && (
              (props.props.topic && props.props.topic.cluster_id && index === 0) && (
                <g>
                  <text x={x + 5} y={y + 15} fill="white">
                    {root.name}
                  </text>
                </g>
              )
            )}
          </>
        );
      }
    };

    return SVGContent;
  }, []);

  return (
    <CustomizedTreeMapNode props={props} />
  );
}